'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Sparkles } from 'lucide-react';
import { Trans } from 'react-i18next';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CarouselBanner, CharacterCard } from '~/components/characters';
import { withPromoCards } from '~/components/characters/promo-grid';
import { StoriesRow } from '~/components/characters/stories-row';
import { useExploreFiltersOptional } from '~/components/navigation/explore-filter-context';
import { matchesCategory } from '~/lib/catalog-categories';
import { useFavorites } from '~/lib/hooks/use-favorites';
import {
  type CharacterListItem,
  type StoryCharacter,
  getPublicCharacters,
} from '~/lib/server/actions/character-actions';

import { FeaturedFilterBar, type FeaturedView } from './featured-filter-bar';

interface LandingPageClientProps {
  initialCharacters: CharacterListItem[];
  storyCharacters: StoryCharacter[];
  /** Hero H1 override for SEO landing variants (plain + gradient part) */
  heroTitlePlain?: string;
  heroTitleGradient?: string;
  heroSubtitle?: string;
  /**
   * Only characters at least this old (the MILF lander: 30+, the same rule as
   * the catalog's MILF chip). Applied on top of the sidebar filters, so it
   * survives the full-catalog top-up.
   */
  minAge?: number;
}

const INITIAL_VISIBLE = 12;
const LOAD_MORE_STEP = 12;

export function LandingPageClient({
  initialCharacters,
  storyCharacters,
  heroTitlePlain = 'Find Your ',
  heroTitleGradient = 'Realistic AI Girlfriend Online',
  heroSubtitle = 'Start a private connection with your AI girlfriend online for chat and talk. Edem AI is the best AI girlfriend simulator - NO filter & NO restrictions.',
  minAge,
}: LandingPageClientProps) {
  const router = useRouter();
  const { openAuthModal } = useAuthModal();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [allCharacters, setAllCharacters] =
    useState<CharacterListItem[]>(initialCharacters);
  const loaderRef = useRef<HTMLDivElement>(null);
  // Category chip + sort from the filter bar (gender/style live in context).
  const [view, setView] = useState<FeaturedView>({
    category: 'all',
    sort: 'newest',
  });
  const { signedIn, ids: favoriteIds } = useFavorites();

  // The server ships only the first 48 characters (serializing all 1000 put
  // ~350KB of flight data in every HTML response). The full catalog is
  // topped up ON DEMAND — first filter touch or first infinite-scroll hit —
  // instead of a blanket 1.5s timer that competed with the LCP window on
  // throttled mobile connections for every visitor, browser or not.
  const toppedUpRef = useRef(false);
  const topUpCatalog = useCallback(() => {
    if (toppedUpRef.current || initialCharacters.length < 48) return;
    toppedUpRef.current = true;

    getPublicCharacters(1000, 0)
      .then(({ characters: all }) => {
        if (all.length > initialCharacters.length) setAllCharacters(all);
      })
      .catch(() => {
        toppedUpRef.current = false;
        // The first 48 remain browsable; a failed top-up is not an error.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useExploreFiltersOptional();

  // Whether the Trans gender filter is active (used in filtering below).
  const isTransSelected = filters?.isFilterSelected('Trans') ?? false;

  // Auto-open auth modal when redirected from protected routes.
  // Reads window.location instead of useSearchParams: this page prerenders
  // statically, and useSearchParams would CSR-bail the whole grid out of the
  // static HTML. A mount-only URL read has no business in the server render.
  useEffect(() => {
    const authParam = new URLSearchParams(window.location.search).get('auth');
    if (authParam === 'sign-up' || authParam === 'sign-in') {
      openAuthModal(authParam);
      router.replace('/', { scroll: false });
    }
  }, [openAuthModal, router]);

  // Filter characters based on all sidebar filters
  const filteredCharacters = useMemo(() => {
    if (!filters) {
      // Fallback: no context, show all
      return allCharacters;
    }

    const { genderFilter, styleFilter, tagFilters } = filters;
    const transSelected = filters.isFilterSelected('Trans');

    return allCharacters.filter((char) => {
      if (minAge && (char.age ?? 0) < minAge) return false;

      // Gender filter (dedicated field)
      if (transSelected) {
        if (char.gender !== 'trans') return false;
      } else if (genderFilter && char.gender !== genderFilter) {
        return false;
      }

      // Style filter (dedicated field)
      if (styleFilter && (char.style ?? 'realistic') !== styleFilter)
        return false;

      // Tag-based filters: body type, age range, personality, content tags
      // All non-gender/style filters are matched against the character's tags array
      if (tagFilters.length > 0) {
        const charTags = char.tags ?? [];
        const charTagsLower = charTags.map((t) => t.toLowerCase());
        for (const tag of tagFilters) {
          if (!charTagsLower.includes(tag.toLowerCase())) return false;
        }
      }

      // Body type filter — check tags (e.g., "Busty", "Petite", "Athletic")
      const bodyTypeLabel = filters.selectedFilters.find((f) =>
        ['Busty', 'Petite', 'Athletic'].includes(f),
      );
      if (bodyTypeLabel) {
        const charTags = char.tags ?? [];
        const charTagsLower = charTags.map((t) => t.toLowerCase());
        if (!charTagsLower.includes(bodyTypeLabel.toLowerCase())) return false;
      }

      // Age range filter — match against the character's numeric age field.
      const ageRangeLabel = filters.selectedFilters.find((f) =>
        ['18-21', '22-30', '30-35', '35+'].includes(f),
      );
      if (ageRangeLabel) {
        const a = char.age;
        if (a == null) return false;
        const inRange =
          ageRangeLabel === '18-21'
            ? a >= 18 && a <= 21
            : ageRangeLabel === '22-30'
              ? a >= 22 && a <= 30
              : ageRangeLabel === '30-35'
                ? a >= 30 && a <= 35
                : a >= 35; // '35+'
        if (!inRange) return false;
      }

      return true;
    });
  }, [
    allCharacters,
    minAge,
    filters?.genderFilter,
    filters?.styleFilter,
    filters?.tagFilters,
    filters?.selectedFilters,
    isTransSelected,
  ]);

  // Category chip and sort on top of the sidebar filters.
  const viewCharacters = useMemo(() => {
    const inCategory =
      view.category === 'all'
        ? filteredCharacters
        : view.category === 'new'
          ? filteredCharacters.filter((c) => c.isNew)
          : filteredCharacters.filter((c) =>
              matchesCategory(c, view.category, favoriteIds),
            );
    if (view.sort !== 'popular' && view.category !== 'popular') {
      return inCategory;
    }
    return [...inCategory].sort(
      (a, b) => (b.conversationCount ?? 0) - (a.conversationCount ?? 0),
    );
  }, [filteredCharacters, view, favoriteIds]);

  // Reset visible count when filters change — and make sure the full catalog
  // is loaded the moment any filter engages, so filtering covers all
  // characters, not just the served 48.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);

    const anyFilterActive = Boolean(
      filters &&
      (filters.genderFilter ||
        filters.styleFilter ||
        filters.tagFilters.length > 0 ||
        filters.selectedFilters.length > 0),
    );
    const viewActive = view.category !== 'all' || view.sort !== 'newest';
    if (anyFilterActive || viewActive) topUpCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters?.genderFilter,
    filters?.styleFilter,
    filters?.tagFilters,
    filters?.selectedFilters,
    view,
  ]);

  const visibleCharacters = viewCharacters.slice(0, visibleCount);
  const hasMore = visibleCount < viewCharacters.length;

  // Load more when sentinel enters viewport. The first hit also tops up the
  // full catalog so scrolling never dead-ends at the served 48.
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting) {
        topUpCatalog();
        if (hasMore) {
          setVisibleCount((prev) => prev + LOAD_MORE_STEP);
        }
      }
    },
    [hasMore, topUpCatalog],
  );

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersection]);

  return (
    // Background lives on the shared parent (page.tsx) so the hero and the
    // content sections form one seamless surface. No overflow-hidden here —
    // the scroll glow must be free to blend past the hero into the sections
    // below (clipping it created a hard horizontal line at the boundary);
    // the shared parent's overflow-hidden still guards against h-scroll.
    <div className="relative">
      {/* Purple glow. Static: the scroll-following ambience is ScrollGlow's
          job (site-layout-wrapper) — this page ran a SECOND rAF+lerp scroll
          loop for the same effect, a direct INP cost. No will-change: a
          promoted 1240x660 layer with 160px blur was one of four expensive
          first-frame rasters in front of the LCP banner. */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 z-0 h-[440px] w-[860px] rounded-full blur-[120px] sm:h-[560px] sm:w-[1040px] lg:h-[660px] lg:w-[1240px] lg:blur-[160px]"
        style={{
          transform: 'translate(-50%, 0)',
          background:
            'radial-gradient(ellipse at center, rgba(160,58,200,0.55) 0%, rgba(126,45,168,0.32) 45%, rgba(80,20,110,0) 75%)',
        }}
      />

      {/* Hero Carousel */}
      <CarouselBanner />

      {/* Stories row — "Choose Your Spark" */}
      <StoriesRow stories={storyCharacters} />

      {/* Characters Section */}
      <section className="relative z-10 pt-3 pb-8 sm:pt-4 sm:pb-12 md:pb-16">
        {/* Widened past Tailwind's breakpoint-capped `container` so the 4-up
            grid renders larger cards (~324px) on wide screens. */}
        <div className="mx-auto px-3 sm:px-4" style={{ maxWidth: '1400px' }}>
          {/* Hero filter section — blends into the page (no card edges) */}
          <div className="relative z-30 mb-5 sm:mb-6">
            {/* Soft purple bloom behind the heading — blurred so it fades into
                the page on all sides with no visible border. */}
            <div
              className="pointer-events-none absolute"
              style={{
                top: '50%',
                left: '58%',
                width: '560px',
                height: '280px',
                maxWidth: '90%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(196,94,210,0.32)',
                borderRadius: '50%',
                filter: 'blur(110px)',
              }}
            />
            <div className="relative">
              <h1 className="mb-2 text-3xl font-bold tracking-tight text-balance sm:text-4xl md:text-[44px] md:leading-[1.1]">
                <span className="text-white">{heroTitlePlain}</span>
                <span
                  style={{
                    background: 'linear-gradient(90deg, #ff9ed2, #ec4899)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  {heroTitleGradient}
                </span>
              </h1>
              <p className="mb-5 max-w-3xl text-sm text-white/60 sm:text-base">
                {heroSubtitle}
              </p>
              <FeaturedFilterBar
                view={view}
                onChange={setView}
                signedIn={signedIn}
              />
            </div>
          </div>

          {viewCharacters.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center sm:py-20">
              <div className="from-eros-pink/20 to-eros-purple/20 mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br sm:mb-6 sm:h-20 sm:w-20">
                <Sparkles className="text-eros-pink h-8 w-8 sm:h-10 sm:w-10" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-white sm:mb-3 sm:text-2xl">
                <Trans
                  i18nKey={
                    allCharacters.length === 0
                      ? 'marketing:landing.emptyTitleNone'
                      : 'marketing:landing.emptyTitleNoMatch'
                  }
                />
              </h3>
              <p className="mb-4 max-w-md text-sm text-gray-400 sm:mb-6 sm:text-base">
                <Trans
                  i18nKey={
                    allCharacters.length === 0
                      ? 'marketing:landing.emptySubtitleNone'
                      : 'marketing:landing.emptySubtitleNoMatch'
                  }
                />
              </p>
              <Link
                href="/create"
                className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-6 py-3 text-sm font-semibold text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] sm:px-8 sm:text-base"
              >
                <Trans i18nKey="marketing:landing.createFirst" />
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 lg:gap-4">
                {withPromoCards(
                  visibleCharacters.map((character, index) => (
                    <CharacterCard
                      key={character.id}
                      id={character.id}
                      slug={character.slug}
                      name={character.name}
                      age={character.age ?? undefined}
                      ethnicity={character.ethnicity}
                      image={
                        character.avatarUrl ||
                        `https://placehold.co/300x400/ec4899/ffffff?text=${encodeURIComponent(character.name)}`
                      }
                      description={character.description || undefined}
                      status={character.isNew ? 'NEW' : null}
                      tags={character.tags || undefined}
                      // Only the first grid row races the network early —
                      // 8 high-priority avatar preloads were competing with
                      // the LCP banner's own fetch.
                      priority={index < 4}
                      noHoverScale={index === 0}
                    />
                  )),
                )}
              </div>
              {/* Intersection sentinel for infinite scroll */}
              <div ref={loaderRef} className="h-4" />
            </>
          )}

          <div className="mt-8 text-center sm:mt-12">
            <Link
              href="/catalog"
              className="border-eros-pink text-eros-pink hover:bg-eros-pink inline-block rounded-xl border px-6 py-2.5 text-sm font-semibold transition-colors hover:text-white sm:px-8 sm:py-3 sm:text-base"
            >
              <Trans i18nKey="marketing:landing.viewAll" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
